/**
 * Content Page - Content/Video performance analytics
 *
 * Shows:
 * - Summary cards (Total Videos, Total Calls, Total Cash Collected)
 * - Metric filter buttons for sorting
 * - Video cards grid with thumbnails and performance metrics
 * - Detailed metrics table
 */

import { useState, useEffect, useCallback, useMemo, type MouseEvent } from "react";
import { motion } from "framer-motion";
import { PageHeader, StatCard, StatCardSkeleton } from "../components";
import { useDateRange } from "../context/DateRangeContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

// Icons
const VideoIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PercentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat("en-US").format(value);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

// Metric filter options
const METRIC_FILTERS = [
  { value: "cashCollected", label: "Cash Collected", icon: DollarIcon },
  { value: "showRate", label: "Show Rate", icon: PercentIcon },
  { value: "closeRate", label: "Close Rate", icon: PercentIcon },
  { value: "aov", label: "AOV", icon: TrendingUpIcon },
  { value: "cashPerCall", label: "Cash per Call", icon: DollarIcon },
  { value: "avgCashPerApplication", label: "Cash/Application", icon: DollarIcon },
  { value: "avgCashPerOptin", label: "Cash/Opt-in", icon: DollarIcon },
] as const;

type MetricFilterValue = typeof METRIC_FILTERS[number]["value"];

interface VideoMetrics {
  videoId: string;
  sourceContent: string;
  title: string;
  thumbnailUrl: string;
  videoUrl: string;
  totalCalls: number;
  callsTaken: number;
  callsDue: number;
  callsClosed: number;
  noShows: number;
  cashCollected: number;
  revenueGenerated: number;
  showRate: number;
  closeRate: number;
  noShowRate: number;
  aov: number;
  cashPerCall: number;
  applications: number;
  optins: number;
  bookedCalls: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
}

interface ContentTotals {
  totalVideos: number;
  totalCalls: number;
  totalCashCollected: number;
  totalRevenue: number;
  totalClosed: number;
  totalApplications: number;
  totalOptins: number;
}

const ITEMS_PER_PAGE = 8;

// Video Thumbnail component with proper loading states
function VideoThumbnail({ thumbnailUrl, title, videoUrl }: { thumbnailUrl: string; title: string; videoUrl: string }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const showPlaceholder = !thumbnailUrl || imageError;

  return (
    <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)]">
      {/* Gradient placeholder is always the background */}
      {showPlaceholder && (
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <div className="text-white/80">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      )}

      {/* Actual thumbnail image - on top of placeholder */}
      {thumbnailUrl && !imageError && (
        <img
          src={thumbnailUrl}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-300 z-10"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}

      {/* Overlay on hover - highest z-index */}
      {videoUrl && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center z-20">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 text-white text-sm font-medium">
            <ExternalLinkIcon />
            <span>Watch on YouTube</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function Content() {
  const { formatForApi } = useDateRange();
  const [isLoading, setIsLoading] = useState(true);
  const [videos, setVideos] = useState<VideoMetrics[]>([]);
  const [totals, setTotals] = useState<ContentTotals | null>(null);
  const [sortBy, setSortBy] = useState<MetricFilterValue>("cashCollected");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("startDate", apiDates.startDate);
      params.set("endDate", apiDates.endDate);
      params.set("sortBy", sortBy);

      const response = await fetch(`/api/content/metrics?${params}`);
      const result = await response.json();

      if (result.success) {
        setVideos(result.data.videos);
        setTotals(result.data.totals);
      }
    } catch (error) {
      console.error("Failed to fetch content metrics:", error);
    } finally {
      setIsLoading(false);
    }
  }, [formatForApi, sortBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);

  const slugify = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);

  const handleDownloadCsv = async (video: VideoMetrics, event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      setDownloadingId(video.videoId);
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({
        startDate,
        endDate,
        videoId: video.videoId,
      });
      const response = await fetch(`/api/content/lead-export?${params.toString()}`);
      if (!response.ok) {
        throw new Error("Failed to export CSV");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeTitle = slugify(video.title || video.videoId);
      link.href = url;
      link.download = `content-leads-${safeTitle || video.videoId}-${startDate}-${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download CSV:", error);
    } finally {
      setDownloadingId(null);
    }
  };

  // Pagination
  const totalPages = Math.ceil(videos.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentVideos = videos.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Get close rate color
  const getCloseRateColor = (rate: number) => {
    if (rate >= 65) return "text-green-600 dark:text-green-400";
    if (rate >= 55) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  // Only show skeleton on initial load, not when filtering (keep old data visible)
  if (isLoading && !totals) {
    return (
      <div className="space-y-6">
        <PageHeader title="Content Performance" subtitle="Track performance metrics for your Social Media content" />

        {/* Stats Summary Skeleton */}
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Filter Skeleton */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {METRIC_FILTERS.map((_, i) => (
            <div key={i} className="h-10 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>

        {/* Video Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Card key={i} className="overflow-hidden">
              <div className="aspect-video bg-muted animate-pulse" />
              <CardContent className="p-4 space-y-3">
                <div className="h-5 bg-muted rounded animate-pulse w-3/4" />
                <div className="space-y-2">
                  {[0, 1, 2, 3].map((j) => (
                    <div key={j} className="flex justify-between">
                      <div className="h-4 bg-muted rounded animate-pulse w-24" />
                      <div className="h-4 bg-muted rounded animate-pulse w-16" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Content Performance" subtitle="Track performance metrics for your Social Media content" />

        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <VideoIcon />
            <p className="text-lg font-medium mt-4">No video data found</p>
            <p className="text-sm text-muted-foreground mt-2">
              Add YouTube video IDs to your call data to see metrics here
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Content Performance" subtitle="Track performance metrics for your Social Media content" />

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Total Videos</p>
                  <p className="text-xl md:text-3xl font-bold">{totals?.totalVideos || 0}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-[var(--brand-primary)]/10 flex items-center justify-center">
                  <span className="text-[var(--brand-primary)]"><VideoIcon /></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Total Calls</p>
                  <p className="text-xl md:text-3xl font-bold">{formatNumber(totals?.totalCalls || 0)}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <span className="text-blue-500"><PhoneIcon /></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Total Cash Collected</p>
                  <p className="text-xl md:text-3xl font-bold">{formatCurrency(totals?.totalCashCollected || 0)}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                  <span className="text-green-500"><DollarIcon /></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Metric Filter Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2"
      >
        {METRIC_FILTERS.map((filter) => {
          const Icon = filter.icon;
          const isActive = sortBy === filter.value;

          return (
            <Button
              key={filter.value}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => setSortBy(filter.value)}
              className={`h-10 w-full text-xs sm:text-sm transition-all ${isActive ? "bg-[var(--brand-primary)] text-white shadow-md hover:bg-[var(--brand-primary)]/90" : ""
                }`}
            >
              <Icon />
              <span className="ml-1 sm:ml-2 truncate">{filter.label}</span>
            </Button>
          );
        })}
      </motion.div>

      {/* Video Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        {currentVideos.map((video, index) => (
          <motion.div
            key={video.videoId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <Card
              className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => video.videoUrl && window.open(video.videoUrl, "_blank")}
            >
              {/* Thumbnail */}
              <VideoThumbnail
                thumbnailUrl={video.thumbnailUrl}
                title={video.title}
                videoUrl={video.videoUrl}
              />

              {/* Content */}
              <CardContent className="p-4 space-y-3">
                {/* Title + Actions */}
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-sm line-clamp-2 min-h-[2.5rem]">
                    {video.title}
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-xs"
                    onClick={(event) => handleDownloadCsv(video, event)}
                    disabled={downloadingId === video.videoId}
                    title="Download CSV"
                  >
                    {downloadingId === video.videoId ? "..." : "CSV"}
                  </Button>
                </div>

                {/* Metrics */}
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Cash Collected</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(video.cashCollected)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Close Rate</span>
                    <span className={`font-semibold ${getCloseRateColor(video.closeRate)}`}>
                      {formatPercent(video.closeRate)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Show Rate</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {formatPercent(video.showRate)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Applicants</span>
                    <span className="font-semibold">{formatNumber(video.applications)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">$/Applicant</span>
                    <span className="font-semibold">{formatCurrency(video.avgCashPerApplication)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Opt-ins</span>
                    <span className="font-semibold">{formatNumber(video.optins)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">$/Opt-in</span>
                    <span className="font-semibold">{formatCurrency(video.avgCashPerOptin)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Booked Calls</span>
                    <span className="font-semibold">{formatNumber(video.bookedCalls)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Closed Calls</span>
                    <span className="font-semibold">{formatNumber(video.callsClosed)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">AOV</span>
                    <span className="font-semibold text-purple-600 dark:text-purple-400">
                      {formatCurrency(video.aov)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-2 mt-8 mb-4 p-4 bg-card border border-border rounded-lg"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeftIcon />
            <span className="ml-1">Previous</span>
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentPage(page)}
                className={`min-w-[2.5rem] ${currentPage === page ? "bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/90" : ""
                  }`}
              >
                {page}
              </Button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            <span className="mr-1">Next</span>
            <ChevronRightIcon />
          </Button>
        </motion.div>
      )}

      {/* Page Info */}
      <div className="text-center text-sm text-muted-foreground">
        Page {currentPage} of {totalPages} | Showing {currentVideos.length} videos (Total: {videos.length})
      </div>

      {/* Detailed Metrics Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-card rounded-xl border border-border"
      >
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold">Content Performance Details</h3>
          <p className="text-sm text-muted-foreground">Detailed metrics for all tracked content</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-4 font-medium text-muted-foreground">Video Title</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Total Calls</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Closed</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Show Rate</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Close Rate</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Cash Collected</th>
                <th className="text-right p-4 font-medium text-muted-foreground">AOV</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Cash/Call</th>
              </tr>
            </thead>
            <tbody>
              {videos.slice(0, 20).map((video, i) => (
                <tr
                  key={video.videoId}
                  className={`border-b border-border hover:bg-muted/30 transition-colors ${i % 2 === 0 ? "" : "bg-muted/20"}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3 max-w-md">
                      {video.thumbnailUrl && (
                        <img
                          src={video.thumbnailUrl}
                          alt=""
                          className="w-16 h-9 object-cover rounded"
                        />
                      )}
                      <div className="flex flex-col">
                        <span className="font-medium line-clamp-1">{video.title}</span>
                        {video.videoUrl && (
                          <a
                            href={video.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-[var(--brand-primary)] flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View on YouTube
                            <ExternalLinkIcon />
                          </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-right">{formatNumber(video.totalCalls)}</td>
                  <td className="p-4 text-right">{formatNumber(video.callsClosed)}</td>
                  <td className="p-4 text-right">
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {formatPercent(video.showRate)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <span className={`font-medium ${getCloseRateColor(video.closeRate)}`}>
                      {formatPercent(video.closeRate)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      {formatCurrency(video.cashCollected)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="text-purple-600 dark:text-purple-400 font-medium">
                      {formatCurrency(video.aov)}
                    </span>
                  </td>
                  <td className="p-4 text-right">{formatCurrency(video.cashPerCall)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {videos.length > 20 && (
          <div className="p-4 text-center text-sm text-muted-foreground border-t border-border">
            Showing top 20 of {videos.length} videos
          </div>
        )}
      </motion.div>
    </div>
  );
}
